import React from 'react';
import _ from 'underscore';

import { Telemetry } from '~commonInfra/services/Telemetry';
import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { DevicePreviewShowPreviewEvent, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { DevicePreviewModalBuilder } from '~src/devicePreview/utils/DevicePreviewModalBuilder';
import { FavoriteToggle } from '~src/favorites/components/favoriteToggle/FavoriteToggle';
import { IExplorableItemTypeInfo, IFavoritableItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { IViewDetailInfo, IViewInfo } from '~src/views/models/IViewInfo';
import { IWorkbookActions } from '~src/workbook/ServerApi/types/IWorkbookActions';

import { AvailableActions } from '../../state/AvailableActionsForEntityState';
import { LayoutType } from '../../state/LayoutType';
import { SpaceInfo } from '../../state/SpaceInfo';
import { SpacesSiteUrls } from '../spaceComponents/SpacesSiteUrls';
import { PageHeaderStyles } from './PageHeaderStyles';
import { VizViewerWidgetsBar } from './VizViewerWidgetsBar';

export interface VizViewerPageHeaderButtonsProps {
  readonly spaceInfo: SpaceInfo;
  readonly siteUrls: SpacesSiteUrls;
  readonly workbookActions: AvailableActions;
  readonly getWorkbookActions: (workbook: IExplorableItemTypeInfo) => void;
  readonly layoutType: LayoutType;
}

export class VizViewerPageHeaderButtons extends React.PureComponent<VizViewerPageHeaderButtonsProps> {
  public componentDidUpdate() {
    if (isValidSpaceInfoForVizView(this.props.spaceInfo) && !this.props.workbookActions) {
      const viewInfo: IViewDetailInfo = this.props.spaceInfo.item as IViewDetailInfo;
      viewInfo.workbook && this.props.getWorkbookActions({ ...viewInfo.workbook, contentType: 'workbook' });
    }
  }

  public render() {
    if (!isValidSpaceInfoForVizView(this.props.spaceInfo)) {
      // don't render this until we get the view info back
      return null;
    }

    const viewInfo: IViewDetailInfo = this.props.spaceInfo.item as IViewDetailInfo;

    return (
      <div className={PageHeaderStyles.VizViewerButtons}>
        <VizViewerWidgetsBar
          view={viewInfo.workbook ? viewInfo : undefined}
          siteUrls={this.props.siteUrls}
          showDevicePreview={this.props.workbookActions
            && (this.props.workbookActions as IWorkbookActions).launchWebAuthoring}
          handleDevicePreviewClick={this.handleDevicePreviewClick}
          layoutType={this.props.layoutType}
        />
      </div>);
  }

  private handleDevicePreviewClick = () => {
    Telemetry.sendEvent<DevicePreviewShowPreviewEvent>({
      type: TelemetryEvents.DevicePreviewShowPreview
    });

    const viewInfo: IViewDetailInfo = this.props.spaceInfo.item as IViewDetailInfo;

    DevicePreviewModalBuilder.openPreview(viewInfo.path);
  };
}


// show a unfavorited button until we get the view info back
const favoriteToggleProps: FavoriteToggle.Props = {
  isFavorite: false,
  onAddFavorite: _.noop,
  onRemoveFavorite: _.noop,
  isTabbable: true,
  buttonType: 'regular'
};

/**
 * Viz Viewer Page Favorite Button
 */
export function isValidSpaceInfoForVizView(spaceInfo: SpaceInfo) {
  // First, run.ts changes the NavLocation in the state. That means the NavLocation is 'viz' before
  // we have updated the space info with the correct item. So we need to check that the content type is correct.
  // Second, VizViewCtrl nullifies the space info's item until it gets the view info back, so we need to
  // check that the item is not null before using it.
  return (spaceInfo.item && spaceInfo.item.contentType === 'view');
}

export interface VizViewerFavoriteButtonProps {
  readonly spaceInfo: SpaceInfo;
  readonly addContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => void;
}

export class VizViewerFavoriteButton extends React.PureComponent<VizViewerFavoriteButtonProps> {

  public render() {
    if (isValidSpaceInfoForVizView(this.props.spaceInfo)) {
      // show a unfavorited button until we get the view info back
      const viewInfo: IViewInfo = this.props.spaceInfo.item as IViewInfo;
      favoriteToggleProps.isFavorite = viewInfo.favorite;
      favoriteToggleProps.onAddFavorite = this.onAddFavorite;
      favoriteToggleProps.onRemoveFavorite = this.onRemoveFavorite;
    }

    return (
      <div style={{marginLeft: '6px', ...CommonStyles.Layout.BlockLeftAndVerticalCenter}}>
        <FavoriteToggle {...favoriteToggleProps} />
      </div>
    );
  }

  private onAddFavorite = () => {
    const viewInfo: IViewInfo = this.props.spaceInfo.item as IViewInfo;
    this.props.addContentFavorite(viewInfo);
  };

  private onRemoveFavorite = () => {
    const viewInfo: IViewInfo = this.props.spaceInfo.item as IViewInfo;
    this.props.removeContentFavorite(viewInfo);
  };
}

